<template>
  <div>
    <RctNavbar :links="navLinks" logo-href="https://ridgecodetech.com">
    </RctNavbar>

    <!-- HERO -->
    <section class="hero">
      <div class="hero-content">
        <p class="hero-eyebrow">Custom Development · Athens, AL</p>
        <h1 class="hero-headline">Built to spec,<br><em>start to finish.</em></h1>
        <p class="hero-sub">Full-stack web applications, APIs, and backend systems built exactly to your requirements — from database schema to deployed UI, without shortcuts or off-the-shelf fillers.</p>
        <div class="hero-actions">
          <RctButton href="#contact">Start a Project</RctButton>
          <RctButton variant="ghost" href="#what-we-build">See What We Build →</RctButton>
        </div>
      </div>
      <div class="ridge-divider">
        <svg viewBox="0 0 1130 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="80" width="1130" height="40" fill="#f5f8ff"/>
          <path d="M0,120 L0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92 L1130,120 Z" fill="#f5f8ff"/>
          <path d="M0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92" fill="none" stroke="#becdea" stroke-width="1.5" opacity="0.6"/>
        </svg>
      </div>
    </section>

    <!-- WHAT WE BUILD -->
    <section class="services" id="what-we-build">
      <div class="section-inner">
        <p class="section-label reveal">What We Build</p>
        <h2 class="section-heading reveal reveal-delay-1">Full-stack capability,<br>from schema to screen.</h2>
        <div class="services-grid">
          <a class="service-card reveal reveal-delay-1" href="#contact">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="32" height="22" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M12 18l4 4 8-8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Web Applications</h3>
            <p class="service-desc">React and Vue SPAs, server-rendered apps, and multi-tenant platforms. Built for real usage patterns — auth, permissions, data, and all the edge cases.</p>
          </a>
          <a class="service-card reveal reveal-delay-2" href="#contact">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 10h24M6 18h18M6 26h12" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="29" cy="26" r="4" stroke="#7a96c1" stroke-width="1.5"/>
            </svg>
            <h3 class="service-title">APIs &amp; Backend Systems</h3>
            <p class="service-desc">RESTful and GraphQL APIs, data pipelines, background workers, and internal services. Designed for correctness and maintainability — not just getting something running.</p>
          </a>
          <a class="service-card reveal reveal-delay-3" href="#contact">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="18" cy="12" rx="12" ry="4" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M6 12v12c0 2.2 5.4 4 12 4s12-1.8 12-4V12" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M6 18c0 2.2 5.4 4 12 4s12-1.8 12-4" stroke="#7a96c1" stroke-width="1.5"/>
            </svg>
            <h3 class="service-title">Database Design</h3>
            <p class="service-desc">Schema design, migrations, indexing strategy, and query optimization. PostgreSQL and MySQL for transactional systems, with the right tradeoffs for your access patterns.</p>
          </a>
          <a class="service-card reveal reveal-delay-4" href="#contact">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="20" y="4" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="4" y="20" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M26 20v12M20 26h12" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">Third-Party Integrations</h3>
            <p class="service-desc">Payment processors, CRMs, communication APIs, authentication providers, and data services. Clean abstractions that don't become a maintenance burden six months later.</p>
          </a>
        </div>
      </div>
    </section>

    <!-- HOW WE WORK -->
    <section class="process-section" id="process">
      <div class="section-inner">
        <p class="section-label reveal">How It Works</p>
        <h2 class="section-heading reveal reveal-delay-1">Clear process,<br>no surprises.</h2>
        <p class="section-body reveal reveal-delay-2">Custom software has a reputation for going sideways. Here's how we keep it on track.</p>
        <div class="process-grid reveal reveal-delay-3">
          <div class="process-step">
            <div class="process-num">01</div>
            <h3 class="process-title">Define Scope</h3>
            <p class="process-desc">We start with a scoping conversation — what you need, what you already have, and what success looks like. No code until requirements are clear.</p>
          </div>
          <div class="process-step">
            <div class="process-num">02</div>
            <h3 class="process-title">Agree on Deliverables</h3>
            <p class="process-desc">Fixed-scope work gets a written spec and milestone breakdown before we start. You know exactly what you're getting and when.</p>
          </div>
          <div class="process-step">
            <div class="process-num">03</div>
            <h3 class="process-title">Build &amp; Review</h3>
            <p class="process-desc">Work is delivered in reviewable increments. You see progress before it's done — not after a black-box development sprint.</p>
          </div>
          <div class="process-step">
            <div class="process-num">04</div>
            <h3 class="process-title">Ship &amp; Hand Off</h3>
            <p class="process-desc">Deployed, documented, and handed off with full code ownership. You're not dependent on us to keep your software running.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="contact-section" id="contact">
      <div class="section-inner">
        <p class="section-label reveal">Get In Touch</p>
        <h2 class="section-heading reveal reveal-delay-1">Ready to start building?</h2>
        <div class="contact-layout">
          <div class="contact-aside reveal reveal-delay-2">
            <p>Tell me what you're building — the problem you're solving, your timeline, and any technical constraints. I'll respond within one business day.</p>
            <div class="contact-detail">
              <a href="mailto:hello@ridgecodetech.com">hello@ridgecodetech.com</a>
              <a href="https://ridgecodetech.com">ridgecodetech.com</a>
            </div>
          </div>
          <div class="reveal reveal-delay-3">
            <form class="contact-form" novalidate @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">Name</label>
                  <input v-model="formName" type="text" id="name" name="name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="email">Email</label>
                  <input v-model="formEmail" type="email" id="email" name="email" placeholder="you@company.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="service">Project Type</label>
                <select v-model="formService" id="service" name="service" required>
                  <option value="" disabled>What are you building?</option>
                  <option value="Web Application">Web Application</option>
                  <option value="API or Backend System">API or Backend System</option>
                  <option value="Database Design">Database Design</option>
                  <option value="Third-Party Integration">Third-Party Integration</option>
                  <option value="Greenfield Build">Greenfield Build (New Product)</option>
                  <option value="Feature Addition">Feature Addition on Existing Codebase</option>
                  <option value="Other">Other / Not sure yet</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">Message</label>
                <textarea v-model="formMessage" id="message" name="message" placeholder="Describe your project, timeline, budget range, and any technical constraints or existing systems involved..." required></textarea>
              </div>
              <div v-if="formStatus.msg" :class="['form-status', formStatus.type]">{{ formStatus.msg }}</div>
              <RctButton type="submit" :loading="formLoading" :disabled="formLoading">Send Message</RctButton>
            </form>
          </div>
        </div>
      </div>
    </section>

    <AgentChat scope="custom" :agent-url="AGENT_URL" />

    <RctFooter :links="footerLinks">
    </RctFooter>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { RctNavbar, RctButton, RctFooter, AgentChat } from '@rct/ui';
import { FOOTER_LINKS, WORKER_URL, AGENT_URL, isValidEmail } from '@rct/shared';

const navLinks = [
  { href: '#what-we-build', label: 'Services'      },
  { href: '#process',       label: 'Process'       },
  { href: '#contact',       label: 'Contact'       },
  { href: '/sign-in',       label: 'Client Login'  },
];

const footerLinks = [
  { href: 'https://ridgecodetech.com', label: 'Home' },
  ...FOOTER_LINKS.filter((l) => l.href !== 'https://custom.ridgecodetech.com'),
] as { href: string; label: string }[];

const formName    = ref('');
const formEmail   = ref('');
const formService = ref('');
const formMessage = ref('');
const formLoading = ref(false);
const formStatus  = ref<{ type: 'success' | 'error' | ''; msg: string }>({ type: '', msg: '' });

async function handleSubmit() {
  const name    = formName.value.trim();
  const email   = formEmail.value.trim();
  const service = formService.value;
  const message = formMessage.value.trim();

  if (!name || !email || !service || !message) {
    formStatus.value = { type: 'error', msg: 'Please fill in all fields before sending.' };
    return;
  }
  if (!isValidEmail(email)) {
    formStatus.value = { type: 'error', msg: 'Please enter a valid email address.' };
    return;
  }

  formLoading.value = true;
  formStatus.value  = { type: '', msg: '' };

  try {
    const res = await fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, email, service, message }),
    });

    if (res.ok) {
      formStatus.value = { type: 'success', msg: "Message sent — I'll be in touch within one business day." };
      formName.value = formEmail.value = formService.value = formMessage.value = '';
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      formStatus.value = { type: 'error', msg: data.error ?? 'Something went wrong. Please try emailing directly.' };
    }
  } catch {
    formStatus.value = { type: 'error', msg: 'Network error — please email hello@ridgecodetech.com directly.' };
  } finally {
    formLoading.value = false;
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
});
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy:       #0f264f;
  --navy-deep:  #091a38;
  --navy-mid:   #1a3a6e;
  --periwinkle: #becdea;
  --steel:      #7a96c1;
  --pale:       #e1ecff;
  --off-white:  #f5f8ff;
  --white:      #ffffff;
  --text-body:  #2c3e5c;
  --text-muted: #6b82a0;
}

html { scroll-behavior: smooth; }
body { font-family: 'Inter', sans-serif; background: var(--off-white); color: var(--text-body); overflow-x: hidden; }

.reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.65s cubic-bezier(.4,0,.2,1), transform 0.65s cubic-bezier(.4,0,.2,1); }
.reveal.visible { opacity: 1; transform: none; }
.reveal-delay-1 { transition-delay: 0.1s; }
.reveal-delay-2 { transition-delay: 0.2s; }
.reveal-delay-3 { transition-delay: 0.3s; }
.reveal-delay-4 { transition-delay: 0.4s; }

.hero { min-height: 100vh; background: var(--navy-deep); display: flex; flex-direction: column; justify-content: center; padding: 10rem 4rem 0; position: relative; overflow: hidden; }
.hero::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(ellipse 60% 50% at 70% 30%, rgba(122,150,193,0.12) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 20% 70%, rgba(15,38,79,0.8) 0%, transparent 60%); pointer-events: none; }
.hero-content { position: relative; max-width: 820px; }
.hero-eyebrow { font-family: 'Space Grotesk', sans-serif; font-size: 0.75rem; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase; color: var(--steel); margin-bottom: 1.5rem; opacity: 0; animation: fadeUp 0.7s 0.2s cubic-bezier(.4,0,.2,1) forwards; }
.hero-headline { font-family: 'Space Grotesk', sans-serif; font-size: clamp(2.8rem, 6vw, 5rem); font-weight: 700; line-height: 1.08; color: var(--white); letter-spacing: -0.02em; margin-bottom: 1.75rem; opacity: 0; animation: fadeUp 0.7s 0.4s cubic-bezier(.4,0,.2,1) forwards; }
.hero-headline em { font-style: normal; color: var(--periwinkle); }
.hero-sub { font-size: 1.15rem; font-weight: 300; color: var(--periwinkle); line-height: 1.7; max-width: 540px; margin-bottom: 3rem; opacity: 0; animation: fadeUp 0.7s 0.6s cubic-bezier(.4,0,.2,1) forwards; }
.hero-actions { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; opacity: 0; animation: fadeUp 0.7s 0.75s cubic-bezier(.4,0,.2,1) forwards; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }

.ridge-divider { position: relative; width: 100%; height: 120px; background: var(--navy-deep); margin-top: 6rem; }
.ridge-divider svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }

section { padding: 6rem 4rem; }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-label { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.22em; text-transform: uppercase; color: var(--steel); margin-bottom: 1rem; }
.section-heading { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.75rem, 3vw, 2.5rem); font-weight: 700; letter-spacing: -0.02em; color: var(--navy); line-height: 1.2; margin-bottom: 1.5rem; }
.section-body { font-size: 1rem; font-weight: 300; color: var(--text-muted); line-height: 1.8; max-width: 600px; }

.services { background: var(--off-white); }
.services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 2px; margin-top: 3.5rem; border: 2px solid var(--periwinkle); }
.service-card { display: block; padding: 2.5rem 2rem; background: var(--white); border-right: 2px solid var(--periwinkle); transition: background 0.2s; text-decoration: none; color: inherit; cursor: pointer; }
.service-card:last-child { border-right: none; }
.service-card:hover { background: var(--pale); }
.service-icon { width: 36px; height: 36px; margin-bottom: 1.25rem; opacity: 0.7; }
.service-title { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; font-weight: 600; color: var(--navy); margin-bottom: 0.75rem; }
.service-desc { font-size: 0.875rem; line-height: 1.7; color: var(--text-muted); font-weight: 300; }

.process-section { background: var(--navy); }
.process-section .section-label { color: var(--steel); }
.process-section .section-heading { color: var(--pale); }
.process-section .section-body { color: var(--periwinkle); margin-bottom: 3rem; }
.process-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px; border: 2px solid var(--navy-mid); margin-top: 3rem; }
.process-step { background: rgba(255,255,255,0.03); border-right: 2px solid var(--navy-mid); padding: 2.5rem 2rem; transition: background 0.2s; }
.process-step:last-child { border-right: none; }
.process-step:hover { background: rgba(122,150,193,0.07); }
.process-num { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.15em; color: var(--steel); opacity: 0.6; margin-bottom: 1rem; }
.process-title { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; font-weight: 600; color: var(--pale); margin-bottom: 0.75rem; }
.process-desc { font-size: 0.875rem; line-height: 1.7; color: var(--periwinkle); font-weight: 300; }

.contact-section { background: var(--navy-deep); padding: 7rem 4rem; }
.contact-section .section-label { color: var(--steel); }
.contact-section .section-heading { color: var(--pale); }
.contact-layout { display: grid; grid-template-columns: 1fr 1.4fr; gap: 5rem; align-items: start; margin-top: 3rem; }
.contact-aside p { font-size: 0.9rem; color: var(--periwinkle); line-height: 1.8; font-weight: 300; margin-bottom: 2rem; }
.contact-detail { display: flex; flex-direction: column; gap: 0.75rem; }
.contact-detail a { font-family: 'Space Grotesk', sans-serif; font-size: 0.875rem; color: var(--steel); text-decoration: none; border-bottom: 1px solid var(--navy-mid); padding-bottom: 2px; width: fit-content; transition: color 0.2s, border-color 0.2s; }
.contact-detail a:hover { color: var(--pale); border-color: var(--steel); }
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; color: var(--steel); }
.form-group input, .form-group select, .form-group textarea { font-family: 'Inter', sans-serif; font-size: 0.9rem; font-weight: 300; color: var(--pale); background: rgba(255,255,255,0.05); border: 1px solid var(--navy-mid); border-radius: 3px; padding: 0.8rem 1rem; outline: none; transition: border-color 0.2s, background 0.2s; width: 100%; }
.form-group select option { background: var(--navy-deep); color: var(--pale); }
.form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--steel); background: rgba(255,255,255,0.08); }
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder, .form-group textarea::placeholder { color: var(--text-muted); }
.form-status { font-size: 0.875rem; padding: 0.75rem 1rem; border-radius: 3px; }
.form-status.success { background: rgba(122,150,193,0.15); border: 1px solid var(--steel); color: var(--pale); }
.form-status.error   { background: rgba(180,60,60,0.15);  border: 1px solid #b44; color: #f5a0a0; }

@media (max-width: 900px) {
  .process-grid  { grid-template-columns: 1fr 1fr; }
  .process-step  { border-right: none; border-bottom: 2px solid var(--navy-mid); }
  .contact-layout { grid-template-columns: 1fr; gap: 3rem; }
}
@media (max-width: 768px) {
  section { padding: 4rem 1.5rem; }
  .hero { padding: 8rem 1.5rem 0; }
  .services-grid { grid-template-columns: 1fr; }
  .service-card { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .service-card:last-child { border-bottom: none; }
  .process-grid { grid-template-columns: 1fr; }
  .form-row { grid-template-columns: 1fr; }
  .contact-section { padding: 4rem 1.5rem; }
}
@media (prefers-reduced-motion: reduce) {
  *, .hero-eyebrow, .hero-headline, .hero-sub, .hero-actions { animation: none !important; transition: none !important; opacity: 1 !important; transform: none !important; }
  .reveal { opacity: 1; transform: none; }
}
</style>
